import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api.service';
import authService from '../services/auth.service';
import { useNavigate } from 'react-router-dom';
import './TodoList.css';

// Modern Icons
const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const ImageIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
    <circle cx="8.5" cy="8.5" r="1.5"></circle>
    <polyline points="21 15 16 10 5 21"></polyline>
  </svg>
);

const ChevronRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

const LoadingSpinner = () => (
  <div className="loading-spinner">
    <svg className="spinner" viewBox="0 0 50 50">
      <circle
        className="path"
        cx="25"
        cy="25"
        r="20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeMiterlimit="10"
      />
    </svg>
  </div>
);

const TodoList = () => {
  const navigate = useNavigate();

  // State variables from your original component
  const [years, setYears] = useState([new Date().getFullYear()]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [months, setMonths] = useState([]);
  const [days, setDays] = useState([]);
  const [entries, setEntries] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [monthCounts, setMonthCounts] = useState({});
  const [dayCounts, setDayCounts] = useState({});
  const [notes, setNotes] = useState('');
  const [uploadingItemId, setUploadingItemId] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  // NEW State for Custom Tasks
  const [customTasks, setCustomTasks] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskNotes, setNewTaskNotes] = useState('');
  const [isAddingCustomTask, setIsAddingCustomTask] = useState(false);
  const [uploadingTaskId, setUploadingTaskId] = useState(null);
  const [deleteTaskConfirmation, setDeleteTaskConfirmation] = useState(null);
  const [showCompleted, setShowCompleted] = useState(true);

  // API URL
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

  const getUploadsUrl = (imagePath) => {
    if (!imagePath) return '';
    if (/^https?:\/\//i.test(imagePath)) return imagePath;
    const base = String(API_URL || '').replace(/\/+$/, '');
    const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    if (normalizedPath.startsWith('/uploads/')) return `${base}${normalizedPath}`;
    return `${base}/uploads${normalizedPath}`;
  };

  const getActorDisplayName = (actor) => {
    if (!actor) return null;
    const fullName = `${actor.name || ''} ${actor.surname || ''}`.trim();
    if (fullName) return fullName;
    if (actor.username) return actor.username;
    return null;
  };

  const getCompletedByName = (obj) =>
    getActorDisplayName(obj?.completedByEmployee || obj?.completedByUser);

  const getImageUploadedByName = (obj) =>
    getActorDisplayName(obj?.imageUploadedByEmployee || obj?.imageUploadedByUser);

  // Month names in Turkish
  const monthNames = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];

  // Helper function to get days in a month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  // Handle API errors and token expiration
  const handleApiError = useCallback((err, defaultMessage) => {
    console.error('API error:', err);
    if (err.response && (err.response.status === 401 || err.response.status === 403)) {
      authService.logout();
      navigate('/admin/login');
      return;
    }
    setError(err.response?.data?.error || defaultMessage || 'Bir hata oluştu.');
  }, [navigate]);

  // Helper to show success message
  const showSuccessMessage = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  // Fetch years and templates on component mount
  useEffect(() => {
    const checkAuthAndFetchData = async () => {
      if (!authService.isLoggedIn()) {
        navigate('/admin/login');
        return;
      }
      setLoading(true);
      await Promise.all([fetchYears(), fetchTemplates()]);
      setLoading(false);
    };

    checkAuthAndFetchData();
  }, [navigate, handleApiError]);

  // Fetch initial data
  const fetchYears = async () => {
    try {
      const response = await apiService.get('/api/todo/years');
      const data = response.data;
      if (data.length > 0) {
        setYears(data);
      }
    } catch (err) {
      console.error('Error fetching years:', err);
      handleApiError(err, 'Yıllar yüklenirken bir hata oluştu');
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await apiService.get('/api/todo/templates/active');
      setTemplates(response.data);
    } catch (err) {
      console.error('Error fetching templates:', err);
      handleApiError(err, 'Şablonlar yüklenirken bir hata oluştu');
    }
  };
  
  // Reset functions for navigation
  const resetDaySelection = () => {
    setSelectedDay(null);
    setEntries([]);
    setCustomTasks([]);
    setError(null);
    setSuccess(null);
    setIsAddingCustomTask(false);
    setNewTaskTitle(''); 
    setNewTaskDescription(''); 
    setNewTaskNotes('');
  };
  
  const resetMonthSelection = () => {
    setSelectedMonth(null);
    setDays([]);
    resetDaySelection();
  };

  // Select a year
  const handleYearClick = (year) => {
    setSelectedYear(year);
    resetMonthSelection();
    
    const monthsArray = Array.from({ length: 12 }, (_, i) => i + 1);
    setMonths(monthsArray);
    fetchMonthsData(year);
  };

  // Select a month
  const handleMonthClick = (month) => {
    setSelectedMonth(month);
    resetDaySelection();

    const daysInMonth = getDaysInMonth(selectedYear, month);
    const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    setDays(daysArray);
    fetchDaysData(selectedYear, month);
  };

  // Select a day
  const handleDayClick = async (day) => {
    setSelectedDay(day);
    setError(null);
    setSuccess(null);
    setIsAddingCustomTask(false);
    setNewTaskTitle(''); 
    setNewTaskDescription(''); 
    setNewTaskNotes('');

    setLoadingEntries(true);
    setLoadingTasks(true);

    await Promise.all([
        fetchEntriesForDay(selectedYear, selectedMonth, day),
        fetchCustomTasksForDay(selectedYear, selectedMonth, day)
    ]);
  };

  // Handle template selection for template-based entries
  const handleTemplateChange = (e) => {
    const templateId = parseInt(e.target.value);
    setSelectedTemplate(templateId || null);
  };

  // Handle notes change for template-based entries
  const handleNotesChange = (e) => {
    setNotes(e.target.value);
  };

  // API calls for months and days counts
  const fetchMonthsData = async (year) => {
    setLoading(true);
    try {
      const response = await apiService.get(`/api/todo/months?year=${year}`);
      const monthData = response.data;
      const countMap = {};
      monthData.forEach(item => { countMap[item.month] = item.count; });
      setMonthCounts(countMap);
    } catch (err) {
      console.error('Error fetching months:', err);
      handleApiError(err, null); 
    } finally {
      setLoading(false);
    }
  };

  const fetchDaysData = async (year, month) => {
    setLoading(true);
    try {
      const response = await apiService.get(`/api/todo/days?year=${year}&month=${month}`);
      const dayData = response.data;
      const countMap = {};
      dayData.forEach(item => { countMap[item.day] = item.count; });
      setDayCounts(countMap);
    } catch (err) {
      console.error('Error fetching days:', err);
      handleApiError(err, null); 
    } finally {
      setLoading(false);
    }
  };

  // Fetch template-based entries for the selected day
  const fetchEntriesForDay = async (year, month, day) => {
    setLoadingEntries(true);
    setEntries([]);
    try {
      const paddedMonth = String(month).padStart(2, '0');
      const paddedDay = String(day).padStart(2, '0');
      const response = await apiService.get(`/api/todo/entries?year=${year}&month=${paddedMonth}&day=${paddedDay}`);
      setEntries(response.data);
    } catch (err) {
      console.error('Error fetching template entries:', err);
      handleApiError(err, 'Şablon kayıtları yüklenirken bir hata oluştu');
      setEntries([]);
    } finally {
      setLoadingEntries(false);
    }
  };

  // NEW: Fetch Custom Tasks for the day
  const fetchCustomTasksForDay = async (year, month, day) => {
    setLoadingTasks(true);
    setCustomTasks([]);
    try {
        const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const response = await apiService.get(`/api/tasks?date=${date}`);
        setCustomTasks(response.data);
    } catch (err) {
        console.error('Error fetching custom tasks:', err);
        handleApiError(err, 'Özel görevler yüklenirken bir hata oluştu');
        setCustomTasks([]);
    } finally {
        setLoadingTasks(false);
    }
  };

  // Create a new template-based todo entry
  const handleCreateEntry = async (e) => {
    e.preventDefault();
    if (!selectedTemplate) { setError('Lütfen bir şablon seçin'); return; }
    if (!selectedYear || !selectedMonth || !selectedDay) { setError('Lütfen bir tarih seçin'); return; }
    
    setLoading(true);
    setError(null); 
    
    try {
      const date = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
      await apiService.post('/api/todo/entries', { date, template_id: selectedTemplate, notes: notes.trim() || null, items: [] });
      setSelectedTemplate(null); 
      setNotes('');
      showSuccessMessage('Yapılacaklar listesi başarıyla oluşturuldu');
      fetchEntriesForDay(selectedYear, selectedMonth, selectedDay);
      fetchDaysData(selectedYear, selectedMonth);
    } catch (err) {
      console.error('Error creating template entry:', err);
      handleApiError(err, 'Yapılacaklar listesi oluşturulurken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // NEW: Create a new custom task
  const handleCreateCustomTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) { setError('Görev başlığı boş olamaz.'); return; }
    if (!selectedYear || !selectedMonth || !selectedDay) { setError('Lütfen bir tarih seçin'); return; }

    setLoading(true);
    setError(null);
    try {
        const date = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
        const taskData = { date, title: newTaskTitle.trim(), description: newTaskDescription.trim() || null, notes: newTaskNotes.trim() || null };
        await apiService.post('/api/tasks', taskData);
        setNewTaskTitle(''); 
        setNewTaskDescription(''); 
        setNewTaskNotes('');
        setIsAddingCustomTask(false);
        showSuccessMessage('Özel görev başarıyla eklendi');
        fetchCustomTasksForDay(selectedYear, selectedMonth, selectedDay);
        fetchDaysData(selectedYear, selectedMonth);
    } catch (err) {
        console.error('Error creating custom task:', err);
        handleApiError(err, 'Özel görev oluşturulurken bir hata oluştu');
    } finally {
        setLoading(false);
    }
  };

  // Update a template-based todo entry item
  const handleUpdateEntryItem = async (entryItemId, isCompleted, itemNotes = null) => {
    setLoadingEntries(true);
    try {
      const requestBody = { is_completed: isCompleted };
      if (itemNotes !== null) { requestBody.notes = itemNotes; }
      await apiService.put(`/api/todo/entry-items/${entryItemId}`, requestBody);
      fetchEntriesForDay(selectedYear, selectedMonth, selectedDay);
    } catch (err) {
      console.error('Error updating entry item:', err);
      handleApiError(err, 'Yapılacak maddesi güncellenirken bir hata oluştu');
    } finally {
      setLoadingEntries(false);
    }
  };

  // NEW: Update Custom Task
  const handleUpdateCustomTask = async (taskId, updates) => {
    setLoadingTasks(true);
    try {
        const response = await apiService.put(`/api/tasks/${taskId}`, updates);
        const updated = response.data;
        setCustomTasks(prevTasks =>
          prevTasks.map(task => (task.id === taskId ? { ...task, ...updated } : task))
        );
    } catch (err) {
        console.error('Error updating custom task:', err);
        handleApiError(err, 'Özel görev güncellenirken bir hata oluştu');
    } finally {
        setLoadingTasks(false);
    }
  };

  // Upload an image for a template-based todo entry item
  const handleImageUpload = async (entryItemId, imageFile) => {
    try {
      setUploadingItemId(entryItemId);
      if (!imageFile || !imageFile.type.startsWith('image/')) {
        setError('Lütfen geçerli bir görsel dosyası seçin (JPG, PNG, GIF, vb.)');
        setUploadingItemId(null); 
        return;
      }
      const formData = new FormData(); 
      formData.append('image', imageFile);
      await apiService.post(`/api/todo/entry-items/${entryItemId}/upload`, formData);
      fetchEntriesForDay(selectedYear, selectedMonth, selectedDay);
      showSuccessMessage('Görsel başarıyla yüklendi');
    } catch (err) {
      console.error('Error uploading template item image:', err);
      if (err.response) { 
        setError(err.response.data.error || 'Görsel yüklenirken bir hata oluştu');
      } else { 
        setError('Görsel yüklenirken beklenmeyen bir hata oluştu'); 
      }
    } finally {
      setUploadingItemId(null);
    }
  };
  
  // NEW: Handle Custom Task Image Upload
  const handleTaskImageUpload = async (taskId, imageFile) => {
    try {
        setUploadingTaskId(taskId);
        if (!imageFile || !imageFile.type.startsWith('image/')) {
            setError('Lütfen geçerli bir görsel dosyası seçin.');
            setUploadingTaskId(null); 
            return;
        }
        const formData = new FormData(); 
        formData.append('image', imageFile);
        await apiService.post(`/api/tasks/${taskId}/upload`, formData);
        fetchCustomTasksForDay(selectedYear, selectedMonth, selectedDay);
        showSuccessMessage('Görev görseli başarıyla yüklendi');
    } catch (err) {
        console.error('Error uploading custom task image:', err);
        if (err.response) { 
          setError(err.response.data.error || 'Görev görseli yüklenirken bir hata oluştu');
        } else { 
          setError('Görev görseli yüklenirken beklenmeyen bir hata oluştu'); 
        }
    } finally {
        setUploadingTaskId(null);
    }
  };

  // Remove an image from a template-based todo entry item
  const handleRemoveImage = async (entryItemId) => {
    try {
      setUploadingItemId(entryItemId);
      await apiService.delete(`/api/todo/entry-items/${entryItemId}/image`);
      fetchEntriesForDay(selectedYear, selectedMonth, selectedDay);
      showSuccessMessage('Görsel başarıyla kaldırıldı');
    } catch (err) {
      console.error('Error removing template item image:', err);
      handleApiError(err, 'Görsel kaldırılırken bir hata oluştu');
    } finally {
      setUploadingItemId(null);
    }
  };

  // NEW: Handle Remove Custom Task Image
  const handleRemoveTaskImage = async (taskId) => {
    try {
        setUploadingTaskId(taskId);
        await apiService.delete(`/api/tasks/${taskId}/image`);
        fetchCustomTasksForDay(selectedYear, selectedMonth, selectedDay);
        showSuccessMessage('Görev görseli başarıyla kaldırıldı');
    } catch (err) {
        console.error('Error removing custom task image:', err);
        handleApiError(err, 'Görev görseli kaldırılırken bir hata oluştu');
    } finally {
        setUploadingTaskId(null);
    }
  };

  // Delete a template-based todo entry
  const handleDeleteEntry = async (entryId) => {
    setLoadingEntries(true);
    setDeleteConfirmation(null);
    try {
      await apiService.delete(`/api/todo/entries/${entryId}`);
      fetchEntriesForDay(selectedYear, selectedMonth, selectedDay);
      fetchDaysData(selectedYear, selectedMonth);
      showSuccessMessage('Yapılacaklar listesi başarıyla silindi');
    } catch (err) {
      console.error('Error deleting template entry:', err);
      handleApiError(err, 'Yapılacaklar listesi silinirken bir hata oluştu');
    } finally {
      setLoadingEntries(false);
    }
  };

  // NEW: Delete Custom Task
  const handleDeleteCustomTask = async (taskId) => {
    setLoadingTasks(true);
    setDeleteTaskConfirmation(null);
    try {
        await apiService.delete(`/api/tasks/${taskId}`);
        fetchCustomTasksForDay(selectedYear, selectedMonth, selectedDay);
        fetchDaysData(selectedYear, selectedMonth);
        showSuccessMessage('Özel görev başarıyla silindi');
    } catch (err) {
        console.error('Error deleting custom task:', err);
        handleApiError(err, 'Özel görev silinirken bir hata oluştu');
    } finally {
        setLoadingTasks(false);
    }
  };

  // --- Render Functions ---
  // Render breadcrumb navigation
  const renderBreadcrumbs = () => {
    return (
      <div className="modern-breadcrumbs">
        <button 
          className="breadcrumb-item"
          onClick={() => {
            setSelectedYear(null); 
            resetMonthSelection();
          }}
        >
          <CalendarIcon />
          Yapılacaklar
        </button>
        {selectedYear && (
          <>
            <ChevronRightIcon />
            <button 
              className="breadcrumb-item"
              onClick={() => {
                resetMonthSelection();
              }}
            >
              {selectedYear}
            </button>
          </>
        )}
        {selectedMonth && (
          <>
            <ChevronRightIcon />
            <button 
              className="breadcrumb-item"
              onClick={() => {
                resetDaySelection();
              }}
            >
              {monthNames[selectedMonth - 1]}
            </button>
          </>
        )}
        {selectedDay && (
          <>
            <ChevronRightIcon />
            <span className="breadcrumb-current">{selectedDay}</span>
          </>
        )}
      </div>
    );
  };

  // Render years view
  const renderYears = () => {
    return (
      <div className="modern-container">
        <div className="section-header">
          <h2>Yılları Seçin</h2>
          <p>Yapılacaklar listenizi görüntülemek için bir yıl seçin</p>
        </div>
        <div className="modern-grid years-grid">
          {years.map(year => (
            <div 
              key={year} 
              className="modern-card clickable year-card" 
              onClick={() => handleYearClick(year)}
            >
              <CalendarIcon />
              <span>{year}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render months view
  const renderMonths = () => {
    return (
      <div className="modern-container">
        <div className="section-header">
          <h2>{selectedYear} Yılı</h2>
          <p>Hangi ayın yapılacaklar listesini görüntülemek istiyorsunuz?</p>
        </div>
        {loading && <LoadingSpinner />}
        {!loading && months.length > 0 && (
          <div className="modern-grid months-grid">
            {months.map(month => (
              <div 
                key={month} 
                className="modern-card clickable month-card" 
                onClick={() => handleMonthClick(month)}
              >
                <div className="card-content">
                  <h3>{monthNames[month - 1]}</h3>
                  {monthCounts[month] > 0 && (
                    <div className="count-badge">
                      {monthCounts[month]} görev
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render days view
  const renderDays = () => {
    return (
      <div className="modern-container">
        <div className="section-header">
          <h2>{monthNames[selectedMonth - 1]} {selectedYear}</h2>
          <p>Yapılacaklar listesini görüntülemek için bir gün seçin</p>
        </div>
        {loading && <LoadingSpinner />}
        {!loading && days.length > 0 && (
          <div className="modern-grid days-grid">
            {days.map(day => (
              <div 
                key={day} 
                className="modern-card clickable day-card" 
                onClick={() => handleDayClick(day)}
              >
                <div className="day-number">{day}</div>
                {dayCounts[day] > 0 && (
                  <div className="count-badge small">
                    {dayCounts[day]}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render the form to create a new template-based todo entry
  const renderEntryForm = () => {
    return (
      <div className="modern-form-card">
        <div className="form-header">
          <h3>Şablondan Yeni Liste Oluştur</h3>
          <p>Hazır şablonları kullanarak hızlıca yapılacaklar listesi oluşturun</p>
        </div>
        <form onSubmit={handleCreateEntry} className="modern-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="template">Şablon Seçin</label>
              <select 
                id="template" 
                value={selectedTemplate || ''} 
                onChange={handleTemplateChange} 
                className="modern-select" 
                required
              >
                <option value="">Şablon seçin...</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="notes">Genel Notlar</label>
            <textarea 
              id="notes" 
              value={notes} 
              onChange={handleNotesChange} 
              className="modern-textarea" 
              rows="3" 
              placeholder="Bu liste için özel notlarınız..."
            />
          </div>
          <button 
            type="submit" 
            className="modern-button primary" 
            disabled={loading || loadingEntries || loadingTasks || !selectedTemplate}
          >
            {(loading || loadingEntries || loadingTasks) ? (
              <>
                <LoadingSpinner />
                İşlem Sürüyor...
              </>
            ) : (
              <>
                <PlusIcon />
                Liste Oluştur
              </>
            )}
          </button>
        </form>
      </div>
    );
  };

  // NEW: Render Custom Task Form
  const renderCustomTaskForm = () => {
    if (!isAddingCustomTask) {
        return (
          <div className="add-task-section">
            <button 
              onClick={() => { 
                setIsAddingCustomTask(true); 
                setError(null); 
              }} 
              className="modern-button secondary add-task-btn"
              disabled={loadingEntries || loadingTasks}
            >
              <PlusIcon />
              Özel Görev Ekle
            </button>
          </div>
        );
    }
    
    return (
      <div className="modern-form-card">
        <div className="form-header">
          <h3>Yeni Özel Görev</h3>
          <p>Kişiselleştirilmiş görevlerinizi ekleyin</p>
        </div>
        <form onSubmit={handleCreateCustomTask} className="modern-form">
          <div className="form-group">
            <label htmlFor="newTaskTitle">Görev Başlığı *</label>
            <input 
              type="text" 
              id="newTaskTitle" 
              value={newTaskTitle} 
              onChange={(e) => setNewTaskTitle(e.target.value)} 
              className="modern-input" 
              required
              placeholder="Görev başlığını girin..."
            />
          </div>
          <div className="form-group">
            <label htmlFor="newTaskDescription">Açıklama</label>
            <textarea 
              id="newTaskDescription" 
              value={newTaskDescription} 
              onChange={(e) => setNewTaskDescription(e.target.value)} 
              className="modern-textarea" 
              rows="2" 
              placeholder="Görev hakkında detaylar (isteğe bağlı)"
            />
          </div>
          <div className="form-group">
            <label htmlFor="newTaskNotes">Notlar</label>
            <textarea 
              id="newTaskNotes" 
              value={newTaskNotes} 
              onChange={(e) => setNewTaskNotes(e.target.value)} 
              className="modern-textarea" 
              rows="2" 
              placeholder="Kişisel notlarınız (isteğe bağlı)"
            />
          </div>
          <div className="form-actions">
            <button 
              type="button" 
              onClick={() => {
                setIsAddingCustomTask(false); 
                setError(null);
              }} 
              className="modern-button secondary"
            >
              İptal
            </button>
            <button 
              type="submit" 
              className="modern-button primary" 
              disabled={loading || loadingEntries || loadingTasks || !newTaskTitle.trim()}
            >
              {(loading || loadingEntries || loadingTasks) ? (
                <>
                  <LoadingSpinner />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <PlusIcon />
                  Görev Ekle
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    );
  };

  // Render todo entries and custom tasks for the selected day
  const renderDayView = () => {
    const openTasks = customTasks.filter(t => !t.is_completed).length;
    const openEntries = entries.reduce((acc, entry) => acc + entry.items.filter(i => !i.is_completed).length, 0);
    const totalOpen = openTasks + openEntries;
    const visibleCustomTasks = showCompleted ? customTasks : customTasks.filter((t) => !t.is_completed);
    const visibleEntries = (entries || [])
      .map((entry) => ({
        ...entry,
        items: showCompleted ? entry.items : (entry.items || []).filter((i) => !i.is_completed)
      }))
      .filter((entry) => Array.isArray(entry.items) && entry.items.length > 0);

    return (
      <div className="modern-container day-view">
        <div className="day-header">
          <div className="day-title">
            <h2>{selectedDay} {monthNames[selectedMonth - 1]} {selectedYear}</h2>
            <div className="day-stats">
              {totalOpen > 0 ? (
                <span className="stats-badge pending">{totalOpen} açık görev</span>
              ) : (
                <span className="stats-badge completed">
                  <CheckCircleIcon />
                  Tüm görevler tamamlandı
                </span>
              )}
            </div>
          </div>
          <div className="day-controls">
            <label className="show-completed-toggle">
              <input
                type="checkbox"
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
              />
              Tamamlananları göster
            </label>
          </div>
        </div>
        
        {/* Global Messages */}
        {error && (
          <div className="modern-alert error">
            <span>⚠️</span>
            <div>
              <strong>Hata:</strong>
              <p>{error}</p>
            </div>
          </div>
        )}
        {success && (
          <div className="modern-alert success">
            <span>✅</span>
            <div>
              <strong>Başarılı:</strong>
              <p>{success}</p>
            </div>
          </div>
        )}

        {/* Add Task and Entry Forms */}
        <div className="forms-section">
          {renderCustomTaskForm()}
          {renderEntryForm()}
        </div>
        
        {/* Loading indicator */}
        {(loadingEntries || loadingTasks) && (
          <div className="loading-section">
            <LoadingSpinner />
            <p>Görevler yükleniyor...</p>
          </div>
        )}

        {/* Custom Tasks List */}
        {!loadingTasks && visibleCustomTasks.length > 0 && (
          <div className="tasks-section">
            <div className="section-header">
              <h3>Özel Görevler</h3>
              <span className="task-counter">
                {customTasks.filter(t => !t.is_completed).length} / {customTasks.length}
              </span>
            </div>
            <div className="tasks-list">
              {visibleCustomTasks.map(task => (
                <div key={task.id} className={`modern-task-card ${task.is_completed ? 'completed' : ''}`}>
                  <div className="task-header">
                    <div className="task-checkbox-section">
                      <label className="modern-checkbox">
                        <input
                          type="checkbox"
                          checked={!!task.is_completed}
                          onChange={(e) => handleUpdateCustomTask(task.id, { is_completed: e.target.checked })}
                        />
                        <span className="checkmark"></span>
                      </label>
                      <div className="task-title-section">
                        <h4 className={task.is_completed ? 'completed-text' : ''}>{task.title}</h4>
                        {task.description && (
                          <p className="task-description">{task.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="task-actions">
                      {deleteTaskConfirmation === task.id ? (
                        <div className="delete-confirmation-modal">
                          <p>Bu görevi silmek istediğinizden emin misiniz?</p>
                          <div className="confirmation-actions">
                            <button 
                              className="modern-button danger small" 
                              onClick={() => handleDeleteCustomTask(task.id)}
                            >
                              Sil
                            </button>
                            <button 
                              className="modern-button secondary small" 
                              onClick={() => setDeleteTaskConfirmation(null)}
                            >
                              İptal
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          className="delete-button"
                          onClick={() => setDeleteTaskConfirmation(task.id)}
                          title="Görevi Sil"
                        >
                          <TrashIcon />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="task-notes-section">
                    <textarea
                      value={task.notes || ''}
                      onChange={(e) => handleUpdateCustomTask(task.id, { notes: e.target.value })}
                      placeholder="Görev notları ekleyin..."
                      className="modern-textarea small"
                      rows="2"
                    />
                  </div>
                  
                  <div className="task-image-section">
                    {task.image_path ? (
                      <div className="image-display">
                        <img 
                          src={getUploadsUrl(task.image_path)} 
                          alt={task.title || "Görev görseli"} 
                          onError={(e) => {e.target.style.display='none';}} 
                        />
                        {(getImageUploadedByName(task) || task.image_uploaded_at) && (
                          <div className="meta-info">
                            {getImageUploadedByName(task) ? `Görseli yükleyen: ${getImageUploadedByName(task)}` : 'Görsel yüklendi'}
                            {task.image_uploaded_at ? ` • ${new Date(task.image_uploaded_at).toLocaleString('tr-TR')}` : ''}
                          </div>
                        )}
                        <button 
                          className="modern-button danger small remove-image-btn" 
                          onClick={() => handleRemoveTaskImage(task.id)} 
                          disabled={uploadingTaskId === task.id}
                        >
                          {uploadingTaskId === task.id ? 'Kaldırılıyor...' : 'Görseli Kaldır'}
                        </button>
                      </div>
                    ) : (
                      <div className="image-upload-section">
                        <label className="upload-area">
                          <ImageIcon />
                          <span>Görsel Yükle</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => e.target.files && e.target.files[0] && handleTaskImageUpload(task.id, e.target.files[0])}
                            disabled={uploadingTaskId === task.id}
                            className="file-input"
                          />
                        </label>
                        {uploadingTaskId === task.id && (
                          <div className="uploading-status">
                            <LoadingSpinner />
                            <span>Yükleniyor...</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {task.is_completed && (task.completed_at || task.updated_at) && (
                    <div className="completion-info">
                      <CheckCircleIcon />
                      <span>
                        Tamamlandı: {new Date(task.completed_at || task.updated_at).toLocaleString('tr-TR')}
                        {getCompletedByName(task) ? ` • Tamamlayan: ${getCompletedByName(task)}` : ''}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!loadingTasks && customTasks.length > 0 && visibleCustomTasks.length === 0 && (
          <div className="modern-alert info">
            <span>ℹ️</span>
            <div>
              <strong>Bilgi:</strong>
              <p>Tamamlananlar gizli. Açık özel görev yok.</p>
            </div>
          </div>
        )}

        {/* Template-Based Entries List */}
        {!loadingEntries && entries.length > 0 && (
          <div className="entries-section">
            <div className="section-header">
              <h3>Şablon Görevleri</h3>
              <span className="task-counter">
                {entries.reduce((acc, entry) => acc + entry.items.filter(i => !i.is_completed).length, 0)} açık görev
              </span>
            </div>
            <div className="entries-list">
              {visibleEntries.length === 0 ? (
                <div className="modern-alert info">
                  <span>ℹ️</span>
                  <div>
                    <strong>Bilgi:</strong>
                    <p>{showCompleted ? 'Bu tarihte şablon görevi yok.' : 'Tamamlananlar gizli. Açık şablon görevi yok.'}</p>
                  </div>
                </div>
              ) : (
                visibleEntries.map(entry => (
                <div key={entry.id} className="modern-entry-card">
                  <div className="entry-header">
                    <div className="entry-title-section">
                      <h3>{entry.template?.title || 'İsimsiz Şablon'}</h3>
                      <div className="entry-meta">
                        <span className="entry-user">{entry.User?.username || 'Bilinmeyen Kullanıcı'}</span>
                        <span className="entry-date">
                          {new Date(entry.created_at || entry.createdAt).toLocaleDateString('tr-TR')}
                        </span>
                      </div>
                      {entry.notes && (
                        <div className="entry-notes">
                          <strong>Notlar:</strong> {entry.notes}
                        </div>
                      )}
                    </div>
                    <div className="entry-actions">
                      {deleteConfirmation === entry.id ? (
                        <div className="delete-confirmation-modal">
                          <p>Bu listeyi silmek istediğinizden emin misiniz?</p>
                          <div className="confirmation-actions">
                            <button 
                              className="modern-button danger small" 
                              onClick={() => handleDeleteEntry(entry.id)}
                            >
                              Sil
                            </button>
                            <button 
                              className="modern-button secondary small" 
                              onClick={() => setDeleteConfirmation(null)}
                            >
                              İptal
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button 
                          className="delete-button" 
                          onClick={() => setDeleteConfirmation(entry.id)}
                          title="Listeyi Sil"
                        >
                          <TrashIcon />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="entry-items">
                    {Array.isArray(entry.items) ? entry.items.map(item => (
                      <div key={item.id} className={`modern-item-card ${item.is_completed ? 'completed' : ''}`}>
                        <div className="item-header">
                          <label className="modern-checkbox">
                            <input 
                              type="checkbox" 
                              checked={item.is_completed} 
                              onChange={() => handleUpdateEntryItem(item.id, !item.is_completed)} 
                            />
                            <span className="checkmark"></span>
                          </label>
                          <div className="item-content">
                            <h4 className={item.is_completed ? 'completed-text' : ''}>
                              {item.item?.title || 'İsimsiz Madde'}
                            </h4>
                            {item.item?.description && (
                              <p className="item-description">{item.item.description}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="item-notes-section">
                          <textarea 
                            value={item.notes || ''} 
                            onChange={(e) => handleUpdateEntryItem(item.id, item.is_completed, e.target.value)} 
                            placeholder="Madde notları..." 
                            className="modern-textarea small"
                            rows="2"
                          />
                        </div>
                        
                        {item.item?.requires_image && (
                          <div className="item-image-section">
                            {item.image_path ? (
                              <div className="image-display">
                                <img 
                                  src={getUploadsUrl(item.image_path)} 
                                  alt="Todo item" 
                                  onError={(e) => {
                                    e.target.src = `${API_URL}/uploads/error.png`; 
                                    e.target.alt = 'Görsel yüklenemedi';
                                  }}
                                />
                                {(getImageUploadedByName(item) || item.image_uploaded_at) && (
                                  <div className="meta-info">
                                    {getImageUploadedByName(item) ? `Görseli yükleyen: ${getImageUploadedByName(item)}` : 'Görsel yüklendi'}
                                    {item.image_uploaded_at ? ` • ${new Date(item.image_uploaded_at).toLocaleString('tr-TR')}` : ''}
                                  </div>
                                )}
                                <button 
                                  className="modern-button danger small remove-image-btn" 
                                  onClick={() => handleRemoveImage(item.id)} 
                                  disabled={uploadingItemId === item.id}
                                >
                                  {uploadingItemId === item.id ? 'Kaldırılıyor...' : 'Görseli Kaldır'}
                                </button>
                              </div>
                            ) : (
                              <div className="image-upload-section required">
                                <div className="required-indicator">
                                  <span>⚠️ Görsel gerekli</span>
                                </div>
                                <label className="upload-area">
                                  <ImageIcon />
                                  <span>Görsel Seç</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => e.target.files && e.target.files[0] && handleImageUpload(item.id, e.target.files[0])}
                                    disabled={uploadingItemId === item.id}
                                    className="file-input"
                                  />
                                </label>
                                {uploadingItemId === item.id && (
                                  <div className="uploading-status">
                                    <LoadingSpinner />
                                    <span>Yükleniyor...</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {item.is_completed && item.completed_at && (
                          <div className="completion-info">
                            <CheckCircleIcon />
                            <span>
                              Tamamlandı: {new Date(item.completed_at).toLocaleString('tr-TR')}
                              {getCompletedByName(item) ? ` • Tamamlayan: ${getCompletedByName(item)}` : ''}
                            </span>
                          </div>
                        )}
                      </div>
                    )) : (
                      <div className="error-state">
                        <span>⚠️</span>
                        <p>Madde listesi yüklenemedi</p>
                      </div>
                    )}
                  </div>
                </div>
              ))
              )}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loadingEntries && !loadingTasks && entries.length === 0 && customTasks.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">
              <CheckCircleIcon />
            </div>
            <h3>Henüz görev yok</h3>
            <p>Bu gün için henüz yapılacaklar listesi veya özel görev bulunmuyor.</p>
            <p>Yukarıdaki formları kullanarak yeni görevler ekleyebilirsiniz.</p>
          </div>
        )}
      </div>
    );
  };

  // Main render function
  return (
    <div className="modern-todo-list">
      {renderBreadcrumbs()}
      
      {/* Show general loading only when navigating years/months/days */}
      {loading && !selectedDay && (
        <div className="loading-section">
          <LoadingSpinner />
          <p>Yükleniyor...</p>
        </div>
      )}
      
      {!selectedYear && renderYears()}
      {selectedYear && !selectedMonth && renderMonths()}
      {selectedYear && selectedMonth && !selectedDay && renderDays()}
      {selectedYear && selectedMonth && selectedDay && renderDayView()}
    </div>
  );
};

export default TodoList;
